import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, Loader2, LinkIcon } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PasswordFields, passwordPairReady } from '../components/PasswordFields';
import { api } from '../lib/api';

/**
 * Where a welcome email and a reset email both land.
 *
 * One page for both, because from here they are the same act: this person is
 * holding a one-time link and is about to choose a password only they will
 * know. What differs is how they arrived, and that changes nothing about the
 * form.
 *
 * The link is **checked before the form is shown**. Somebody who opens a
 * two-day-old email should be told the link expired immediately, not after
 * choosing and confirming a password — that second version wastes the effort
 * and reads as the app losing their work.
 */
export function SetPasswordPage() {
  const { token = '' } = useParams();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const link = useQuery({
    queryKey: ['reset-link', token],
    queryFn: () => api.checkResetLink(token),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () => api.resetPassword(token, next),
    // A full navigation rather than a route change: there is no session yet,
    // and the sign-in page should be reached fresh.
    onSuccess: () => window.setTimeout(() => window.location.assign('/'), 1200),
    onError: (caught: Error) => setError(caught.message),
  });

  if (link.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Checking the link" />
      </div>
    );
  }

  // Expired, already spent, or never real — all one message. Distinguishing
  // them would tell somebody holding a guessed token which guesses were close.
  if (!link.data?.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-muted">
              <LinkIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            </div>
            <h1 className="font-heading text-xl">This link no longer works</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Set-password links work once and then expire. If you have already used it, sign in
              with the password you chose. If not, ask for a new one.
            </p>
            <a href="/" className="btn-primary mt-5 w-full">
              Go to sign in
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (submit.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-50">
              <CheckCircle2 className="h-6 w-6 text-secondary-600" aria-hidden="true" />
            </div>
            <h1 className="font-heading text-xl">Password set</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Taking you to sign in. Use {link.data.email} and the password you just chose.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ready = passwordPairReady(next, confirm, { email: link.data.email });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="card">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50">
            <KeyRound className="h-6 w-6 text-primary-600" aria-hidden="true" />
          </div>

          <h1 className="font-heading text-xl">Choose your password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            For <strong>{link.data.email}</strong>. Nobody here can see it — not your admin, not
            whoever set up your account.
          </p>

          <form
            className="mt-5 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              submit.mutate();
            }}
          >
            {error && (
              <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
                {error}
              </p>
            )}

            <PasswordFields
              value={next}
              confirm={confirm}
              onValueChange={setNext}
              onConfirmChange={setConfirm}
              email={link.data.email}
              label="Your password"
            />

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={!ready || submit.isPending}
            >
              {submit.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Set my password and sign in
            </button>
          </form>

          <p className="mt-4 text-xs text-muted-foreground">
            Setting a password here signs out every device that was using the old one.
          </p>
        </div>
      </div>
    </div>
  );
}
